export const JOIN_DISPOSE_STORAGE_KEY = 'primus.joinDisposeLosers';

const JOIN_DISPOSE_MODELS = new Set([
  'JoinObjectsOperationModel',
  'NewJoinObjectsOperationModel',
]);

export interface JoinDisposeModel {
  dispose_losers?: string;
  dispose_losers_remember?: boolean;
  has_kulturnav_concept?: boolean;
  join_target_object?: string;
  join_items?: Array<{
    join_item_id?: string;
    join_item_authority_id?: string | null;
    join_item_selected?: boolean;
  }>;
}

export interface JoinDisposeStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface JoinDisposeFormControl {
  setValue(value: string, options?: object): void;
  enable(options?: object): void;
  disable(options?: object): void;
}

export function applyStoredJoinDispose(
  modelData: object | null | undefined,
  objectType: string | undefined,
  storage: Pick<JoinDisposeStorage, 'getItem'>): void {
  if (!modelData || !updateJoinDisposeLock(modelData, objectType)) {
    return;
  }
  const joinModel = asJoinDisposeModel(modelData);
  if (joinModel.has_kulturnav_concept) {
    return;
  }
  const choice = readJoinDisposePreference(storage);
  if (!choice) {
    return;
  }
  joinModel.dispose_losers = choice;
  joinModel.dispose_losers_remember = true;
}

export function updateJoinDisposeLock(
  modelData: object | null | undefined,
  objectType: string | undefined): boolean {
  if (!modelData || !isJoinDisposeModel(objectType)) {
    return false;
  }
  const joinModel = asJoinDisposeModel(modelData);
  if (joinHasKulturNavLoser(joinModel)) {
    joinModel.dispose_losers = 'invalidate';
    joinModel.has_kulturnav_concept = true;
    return true;
  }
  joinModel.has_kulturnav_concept = false;
  return true;
}

export function syncJoinDisposeFormControl(
  modelData: object | null | undefined,
  control: JoinDisposeFormControl | null | undefined): void {
  if (!modelData || !control) {
    return;
  }
  const joinModel = asJoinDisposeModel(modelData);
  if (!isJoinDisposeChoice(joinModel.dispose_losers)) {
    return;
  }
  control.enable({emitEvent: false});
  control.setValue(joinModel.dispose_losers, {emitEvent: false});
  if (joinModel.has_kulturnav_concept) {
    control.disable({emitEvent: false});
  }
}

export function findJoinDisposeFormControl(
  formGroup: { get?(key: string): JoinDisposeFormControl | null; controls?: Record<string, JoinDisposeFormControl> } | null | undefined,
  modelData?: object | null,
): JoinDisposeFormControl | undefined {
  if (!formGroup) {
    return undefined;
  }
  const joinMeta = modelData as { $$meta?: { dispose_losers?: { key?: string; name?: string } } } | undefined;
  const keys = [
    joinMeta?.$$meta?.dispose_losers?.key,
    joinMeta?.$$meta?.dispose_losers?.name,
    'dispose_losers',
  ].filter((key): key is string => !!key);
  for (const key of keys) {
    const control = formGroup.get?.(key) || formGroup.controls?.[key];
    if (control) {
      return control;
    }
  }
  const match = Object.keys(formGroup.controls || {}).find(key => key === 'dispose_losers' || key.endsWith('dispose_losers'));
  return match ? formGroup.controls?.[match] : undefined;
}

export function persistJoinDisposeOnConfirm(
  modelData: object | null | undefined,
  objectType: string | undefined,
  storage: Pick<JoinDisposeStorage, 'setItem' | 'removeItem'>): void {
  if (!modelData || !isJoinDisposeModel(objectType)) {
    return;
  }
  const joinModel = asJoinDisposeModel(modelData);
  if (joinHasKulturNavLoser(joinModel)) {
    return;
  }
  if (joinModel.dispose_losers_remember && isJoinDisposeChoice(joinModel.dispose_losers)) {
    storage.setItem(JOIN_DISPOSE_STORAGE_KEY, joinModel.dispose_losers);
    return;
  }
  storage.removeItem(JOIN_DISPOSE_STORAGE_KEY);
}

function asJoinDisposeModel(modelData: object): JoinDisposeModel {
  return modelData as JoinDisposeModel;
}

function joinHasKulturNavLoser(joinModel: JoinDisposeModel): boolean {
  const items = joinModel.join_items || [];
  const winnerId = joinWinnerId(joinModel);
  return items.some(item => {
    if (!item.join_item_authority_id) {
      return false;
    }
    if (winnerId) {
      return item.join_item_id !== winnerId;
    }
    return !item.join_item_selected;
  });
}

function joinWinnerId(joinModel: JoinDisposeModel): string | undefined {
  if (joinModel.join_target_object) {
    return joinModel.join_target_object;
  }
  return (joinModel.join_items || []).find(item => item.join_item_selected)?.join_item_id;
}

function readJoinDisposePreference(storage: Pick<JoinDisposeStorage, 'getItem'>): string | undefined {
  const value = storage.getItem(JOIN_DISPOSE_STORAGE_KEY);
  return isJoinDisposeChoice(value) ? value : undefined;
}

function isJoinDisposeModel(objectType: string | undefined): boolean {
  return !!objectType && JOIN_DISPOSE_MODELS.has(objectType);
}

function isJoinDisposeChoice(value: string | null | undefined): value is 'invalidate' | 'delete' {
  return value === 'invalidate' || value === 'delete';
}
